import { api, ApiError } from "./api";
import { fmtDateTime } from "./datetime";

declare const marked: { parse: (md: string) => string };
declare const DOMPurify: { sanitize: (html: string) => string };

interface Comment {
  id: string;
  signalId: string;
  userId: string | null;
  username: string;
  body: string;
  createdAt: string;
  canDelete: boolean;
  canEdit: boolean;
}

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderMarkdown(raw: string): string {
  const processed = raw.replace(/\[\[([^\[\]]+)\]\]/g, (_, n: string) => {
    const slug = n.trim().toLowerCase().replace(/\s+/g, "-");
    return `[#${slug}](/my-signals?wikilink=${encodeURIComponent(slug)})`;
  });
  let html = typeof marked !== "undefined"
    ? (marked.parse(processed) as string)
    : escHtml(processed);
  if (typeof DOMPurify !== "undefined") html = DOMPurify.sanitize(html);
  return html;
}

/** Comment timestamps, in the app time zone like every other timestamp.
 *  Browser-local put a comment on a different day from the signal it hangs
 *  off, for anyone working outside that zone. */
function formatDate(iso: string): string {
  return fmtDateTime(iso);
}

/**
 * Mounts a comment thread into `container`.
 * Loads comments from the API and manages submit/edit/delete interactions.
 */
export function attachCommentThread(
  signalId: string,
  container: HTMLElement,
  onCountChange?: (count: number) => void,
): void {
  container.innerHTML = `<div class="ct-loading muted">Loading notes…</div>`;
  void load();

  async function load(): Promise<void> {
    try {
      const comments = await api<Comment[]>(`/signals/${signalId}/comments`);
      render(comments);
    } catch {
      container.innerHTML = `<div class="ct-section"><div class="ct-header"><span class="ct-title">Notes</span></div><p class="muted" style="font-size:13px;">Failed to load notes.</p></div>`;
    }
  }

  function render(comments: Comment[]): void {
    onCountChange?.(comments.length);
    container.innerHTML = "";
    const section = document.createElement("div");
    section.className = "ct-section";

    // Header
    section.innerHTML = `<div class="ct-header"><span class="ct-title">Notes</span></div>`;

    // Comment list
    const list = document.createElement("div");
    list.className = "ct-list";

    if (comments.length === 0) {
      list.innerHTML = `<p class="ct-empty">No notes yet. Be the first to add one.</p>`;
    } else {
      for (const c of comments) {
        list.appendChild(buildCommentEl(c));
      }
    }
    section.appendChild(list);

    // Submit form
    section.appendChild(buildForm());
    container.appendChild(section);
  }

  function buildCommentEl(c: Comment): HTMLElement {
    const el = document.createElement("div");
    el.className = "ct-comment";
    el.dataset["commentId"] = c.id;

    const actionBtns = [
      c.canEdit
        ? `<button class="ct-edit-btn icon-btn" title="Edit note">✎</button>`
        : "",
      c.canDelete
        ? `<button class="ct-delete-btn icon-btn" title="Delete note">✕</button>`
        : "",
    ].join("");

    el.innerHTML = `
      <div class="ct-comment-header">
        <span class="ct-author">${escHtml(c.username)}</span>
        <span class="ct-timestamp muted">${escHtml(formatDate(c.createdAt))}</span>
        <div class="ct-comment-actions">${actionBtns}</div>
      </div>
      <div class="ct-comment-body">${renderMarkdown(c.body)}</div>`;

    // Store raw body for edit mode
    el.dataset["rawBody"] = c.body;

    if (c.canEdit) {
      el.querySelector(".ct-edit-btn")!.addEventListener("click", () => {
        enterEditMode(el, c.id);
      });
    }

    if (c.canDelete) {
      el.querySelector(".ct-delete-btn")!.addEventListener("click", () => {
        if (confirm("Delete this note?")) void deleteComment(c.id);
      });
    }

    // Wire wikilink class
    el.querySelectorAll<HTMLAnchorElement>('a[href^="/my-signals?wikilink="]').forEach((a) => {
      a.classList.add("wikilink-inline");
    });

    return el;
  }

  function enterEditMode(el: HTMLElement, commentId: string): void {
    const bodyDiv = el.querySelector<HTMLDivElement>(".ct-comment-body")!;
    const rawBody = el.dataset["rawBody"] ?? "";

    // Replace rendered body with edit UI
    bodyDiv.innerHTML = "";

    const ta = document.createElement("textarea");
    ta.className = "ct-edit-textarea";
    ta.value = rawBody;
    autoResize(ta);
    ta.addEventListener("input", () => autoResize(ta));

    const footer = document.createElement("div");
    footer.className = "ct-edit-footer";

    const errEl = document.createElement("p");
    errEl.className = "ct-form-err";

    const saveBtn = document.createElement("button");
    saveBtn.className = "ct-submit-btn primary";
    saveBtn.textContent = "Save";

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "btn btn-sm";
    cancelBtn.textContent = "Cancel";

    footer.appendChild(errEl);
    footer.appendChild(cancelBtn);
    footer.appendChild(saveBtn);
    bodyDiv.appendChild(ta);
    bodyDiv.appendChild(footer);

    // Hide action buttons while editing
    const actionsEl = el.querySelector<HTMLElement>(".ct-comment-actions");
    if (actionsEl) actionsEl.style.display = "none";

    ta.focus();

    cancelBtn.addEventListener("click", () => {
      // Restore the rendered view
      bodyDiv.innerHTML = renderMarkdown(rawBody);
      if (actionsEl) actionsEl.style.display = "";
      el.querySelectorAll<HTMLAnchorElement>('a[href^="/my-signals?wikilink="]').forEach((a) => {
        a.classList.add("wikilink-inline");
      });
    });

    saveBtn.addEventListener("click", () => void saveEdit());

    ta.addEventListener("keydown", (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        void saveEdit();
      }
      if (e.key === "Escape") cancelBtn.click();
    });

    async function saveEdit(): Promise<void> {
      errEl.textContent = "";
      const text = ta.value.trim();
      if (!text) { errEl.textContent = "Note cannot be empty."; return; }
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      try {
        await api(`/signals/${signalId}/comments/${commentId}`, {
          method: "PATCH",
          body: { body: text },
        });
        void load(); // reload full thread with updated content
      } catch (err) {
        errEl.textContent = err instanceof ApiError ? err.message : "Failed to save.";
        saveBtn.disabled = false;
        saveBtn.textContent = "Save";
      }
    }
  }

  function buildForm(): HTMLElement {
    const form = document.createElement("div");
    form.className = "ct-form";
    form.innerHTML = `
      <textarea class="ct-textarea" placeholder="Add a note… (markdown + [[wikilinks]] supported)"></textarea>
      <div class="ct-form-footer">
        <p class="ct-form-err"></p>
        <button class="ct-submit-btn primary">Add Note</button>
      </div>`;

    const ta = form.querySelector<HTMLTextAreaElement>(".ct-textarea")!;
    const errEl = form.querySelector<HTMLParagraphElement>(".ct-form-err")!;
    const submitBtn = form.querySelector<HTMLButtonElement>(".ct-submit-btn")!;

    ta.addEventListener("input", () => autoResize(ta));

    ta.addEventListener("keydown", (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        void submit();
      }
    });

    submitBtn.addEventListener("click", () => void submit());

    async function submit(): Promise<void> {
      errEl.textContent = "";
      const text = ta.value.trim();
      if (!text) { errEl.textContent = "Note cannot be empty."; return; }
      submitBtn.disabled = true;
      submitBtn.textContent = "Saving…";
      try {
        await api<Comment>(`/signals/${signalId}/comments`, {
          method: "POST",
          body: { body: text },
        });
        ta.value = "";
        ta.style.height = "auto";
        void load(); // reload full thread to show new comment
      } catch (err) {
        errEl.textContent = err instanceof ApiError ? err.message : "Failed to save.";
        submitBtn.disabled = false;
        submitBtn.textContent = "Add Note";
      }
    }

    return form;
  }

  async function deleteComment(commentId: string): Promise<void> {
    try {
      await api(`/signals/${signalId}/comments/${commentId}`, { method: "DELETE" });
      void load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Failed to delete.");
    }
  }

  function autoResize(ta: HTMLTextAreaElement): void {
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }
}

// Re-export name used by folder_view / my_signals
export { attachCommentThread as attachNoteEditor };
